import React, { useCallback, useMemo } from 'react';
import { convert2CompressedUrl } from '@shakkerai/util-image';
import { SegmentsViewer } from '@starflow/app/components/common/SegmentEditor';
import { translate } from '@shakkerai/util-i18n';
import { useProjectState, useTempState, getTempState, setTempState } from '@starflow/app/store';
import { notifyError } from '@shakkerai/pro-components';
import cs from 'classnames';
import { Image } from './Image';

const maxWidth = 260;
const maxHeight = 186;

const MaskPreview = React.memo(({ width, height, shapes, scale, sourceImage }) => {
  const element = useMemo(() => {
    if (!sourceImage) return null;
    const { width, height } = sourceImage;

    // 以contain形式展示图片
    const scale = Math.min(maxWidth / width, maxHeight / height);
    const realWidth = width * scale;
    const realHeight = height * scale;
    const scaleX = realWidth / width;
    const scaleY = realHeight / height;
    const x = (maxWidth - realWidth) / 2;
    const y = (maxHeight - realHeight) / 2;
    return {
      x,
      y,
      scaleX,
      scaleY,
      width,
      height,
    };
  }, [sourceImage]);

  if (!element || !shapes?.length) return null;
  return <SegmentsViewer scale={scale} shapes={shapes} element={element} width={width} height={height} />;
});

const ImagePreview = React.memo(({ value }) => {
  const { sourceImage, mask } = value || {};

  return (
    <div className="relative">
      <img src={convert2CompressedUrl(sourceImage.url, 520)} className="w-[260px] h-[186px] object-contain" />
      <div className="absolute top-0 left-0 w-full h-full">
        <MaskPreview scale={1} width={260} height={186} shapes={mask?.shapes} sourceImage={sourceImage} />
      </div>
    </div>
  );
});

export const Mask = React.memo((props) => {
  const { title, name, value, onChange } = props;
  const { sourceImage } = value || {};

  const handleSourceImageChange = useCallback(
    (sourceImage) => {
      onChange({ ...value, sourceImage, mask: {} }, name);
    },
    [value, name, onChange],
  );

  const handleOpenMaskEditor = useCallback(() => {
    if (!value?.sourceImage) {
      notifyError(translate('workflow_mask_source_image_tip'));
      return;
    }

    setTempState({
      workflowMaskEditorData: value,
      workflowMaskUpdateCallback: (maskShapes) => {
        onChange({ ...value, mask: { shapes: maskShapes } }, name);
      },
      exclusiveWindow: 'mask-editor',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, name, onChange]);

  return (
    <div>
      {title && (
        <div className="mb-1">
          <span className="text-xs text-[#7f7f7f] leading-[14px]">{title}</span>
        </div>
      )}
      <Image badge={translate('original')} value={sourceImage} onChange={handleSourceImageChange} />
      <div className="mt-2 group flex flex-col items-center justify-center relative h-[188px] overflow-hidden bg-[#f4f6f8] border border-[#ededed] rounded">
        <span className="absolute z-[4] top-1.5 left-1.5 px-3 py-1 bg-black/30 rounded text-xs text-white">蒙版</span>
        {sourceImage ? (
          <>
            <ImagePreview value={value} />
            <div className="absolute z-[3] inset-0 flex items-center justify-center bg-black/70 opacity-0 group-hover:opacity-100">
              <button
                data-trigger-window="mask-editor"
                onClick={handleOpenMaskEditor}
                className="z-2 w-32 h-[34px] bg-white/5 border border-white/50 hover:bg-white/10 hover:border-white/80 rounded text-xs text-white"
              >
                {translate('workflow_mask_draw_button')}
              </button>
            </div>
          </>
        ) : (
          <>
            <span className="text-xs text-[#2d2d2d]">{translate('workflow_mask_source_image_tip')}</span>
            <button
              data-trigger-window="mask-editor"
              onClick={handleOpenMaskEditor}
              className="w-32 h-[34px] mt-2.5 bg-white border border-[#ededed] hover:border-[#dedede] rounded text-xs text-[#2d2d2d] disabled:pointer-events-none disabled:opacity-50"
            >
              {translate('workflow_mask_draw_button')}
            </button>
          </>
        )}
      </div>
    </div>
  );
});

const originImg = (sourceImage, classnames) => {
  return (
    <div className={`${cs('flex items-center', classnames)}`}>
      <img className="w-[40px] h-[52px] object-cover rounded-sm" src={convert2CompressedUrl(sourceImage.url, 40)} />
      {/* TODO starling */}
      <span className="ml-2 text-[#2d2d2d] text-xs font-normal">原图</span>
    </div>
  );
};

const maskImg = (sourceImage, mask, classnames) => {
  return (
    <div className={`${cs('flex items-center', classnames)}`}>
      <div className="relative rounded-sm overflow-hidden">
        <img src={convert2CompressedUrl(sourceImage.url, 40)} className="w-[40px] h-[52px] object-cover" />
        <div className="absolute top-0 left-0">
          <MaskPreview scale={1} width={40} height={52} shapes={mask?.shapes} sourceImage={sourceImage} />
        </div>
      </div>
      {/* TODO starling */}
      <span className="ml-2 text-[#2d2d2d] text-xs font-normal">蒙版</span>
    </div>
  );
};

export const MaskView = React.memo(({ value: { sourceImage, mask }, title }) => {
  return (
    <>
      <div className="p-4 rounded-sm">
        <h5 id="model-checkpoints-entry" className="text-[#2d2d2d] text-xs leading-[14px] font-bold font-gro">
          {title}
        </h5>
        {originImg(sourceImage, 'mt-3')}
        {maskImg(sourceImage, mask, 'mt-3')}
      </div>
      <div className="border-t border-[#efefef]" />
    </>
  );
});
